// Thin WebSocket client used by the Ludo page. Auto-reconnects and re-authenticates
// with the current access token. Consumers subscribe to message types.
//
// [K] Heartbeat + reconnect parity with PHP ludo_client.php initWebSocket():
//   • Send `{type:'ping'}` every 25s so Workerman-style idle timeouts never
//     fire; server replies with `{type:'pong'}` (noop on our side).
//   • On close we enter `isReconnecting` and retry with a flat 3 s delay —
//     PHP uses `setTimeout(initWebSocket, 3000)`; exponential backoff was
//     too slow on flaky mobile connections.
//   • `onStatus` lets the Ludo page render a coloured status chip that
//     matches the PHP #status-box ("Connecting to Server…" / "Reconnecting
//     to Server…" / "Connection Lost! Reconnecting…" / "Ready in Lobby").
import { getAccessToken, api } from '@baaligames/shared-ui/lib/api';

type Listener = (msg: any) => void;
export type WSStatus = 'connecting' | 'open' | 'reconnecting' | 'lost';
type StatusFn = (s: WSStatus) => void;

export class LudoWS {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusFn>();
  private closed = false;
  private reconnecting = false;
  private pingTimer: number | null = null;
  // Flag set when the server's last auth attempt failed (either via a
  // `{type:'error', code:'auth_failed'}` frame or a close(4002)). Next
  // auth() will force a refresh instead of resending the stale token.
  private authFailedOnce = false;

  connect() {
    this.closed = false;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.emitStatus(this.reconnecting ? 'reconnecting' : 'connecting');
    this.ws = new WebSocket(`${proto}://${location.host}/api/ludo/ws`);
    this.ws.addEventListener('open', () => {
      // Preserve reconnecting for auth() to see, then clear it.
      const wasReconnecting = this.reconnecting;
      this.emitStatus('open');
      this.auth(wasReconnecting).finally(() => { this.reconnecting = false; });
    });
    this.ws.addEventListener('message', (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        // If the server rejected our token, mark so the next reconnect
        // refreshes before re-sending. Without this, a 15-minute-old
        // access token causes an infinite `auth_failed → close(4002) →
        // retry → auth_failed` loop the moment the socket blips mid-game.
        if (msg && msg.type === 'error' && msg.code === 'auth_failed') {
          this.authFailedOnce = true;
        }
        for (const l of this.listeners) l(msg);
      } catch {}
    });
    this.ws.addEventListener('close', (ev) => {
      if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
      // Close code 4002 = backend "auth failed" — force refresh before retry.
      if (ev && (ev as CloseEvent).code === 4002) this.authFailedOnce = true;
      if (!this.closed) {
        this.reconnecting = true;
        this.emitStatus('lost');
        // PHP-parity: flat 3 s retry interval, not exponential backoff.
        setTimeout(() => this.connect(), 3000);
      }
    });
    this.ws.addEventListener('error', () => {/* swallow; close handler reconnects */});
  }

  private async auth(isReconnect: boolean = false) {
    // [Reconnect-with-expired-token fix] Access tokens live 15 min (see
    // backend .env JWT_ACCESS_TTL=15m), but a game can easily last
    // longer. Previously we only refreshed when accessToken was missing
    // — a stale-but-present token would send an expired JWT, the server
    // would 4002-close us, we'd retry with the same dead token, and the
    // game would appear to "freeze" on any transient network blip. Now:
    //   • Always refresh on a reconnect cycle (`this.reconnecting`).
    //   • Also refresh after an explicit auth_failed or 4002 close.
    //   • Refresh on first auth if no token yet (fresh mount).
    // The refresh-token cookie is long-lived so this keeps the socket
    // alive across any game length.
    const needsRefresh = !getAccessToken() || isReconnect || this.authFailedOnce;
    if (needsRefresh) {
      await api.tryRefresh();
      this.authFailedOnce = false;
    }
    const token = getAccessToken();
    if (!token || !this.ws) return;
    this.ws.send(JSON.stringify({ type: 'authenticate', token }));
    // [K] PHP heartbeat: 25 000 ms.
    this.pingTimer = window.setInterval(() => this.send({ type: 'ping' }), 25_000);
  }

  onStatus(fn: StatusFn) {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }
  private emitStatus(s: WSStatus) { for (const fn of this.statusListeners) fn(s); }

  send(msg: any) {
    if (!this.ws || this.ws.readyState !== 1) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  on(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  close() {
    this.closed = true;
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
    this.ws?.close();
    this.ws = null;
  }
}
